import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import {
  getLiveDocumentedMedalTotal,
  getLiveInternationalMedals,
  getLiveNationalMedals,
} from "../../utils/medals";

const FALLBACK_SECTIONS = [
  {
    id: "intro",
    section_key: "intro",
    heading: "A Journey of Resilience and Triumph",
    subheading: "From 2015 to global recognition",
    body:
      "Founded in 2015 after bifurcation,  Para Sports Accociation of Andhra Pradesh grew from 7 medals to Paralympic participation and hundreds of national and international podium finishes.",
    image_path: "/images/image-fallback.svg",
    sort_order: 1,
    is_active: true,
  },
];

const normalizeImageFit = (fit) => {
  const value = String(fit || "cover").toLowerCase();
  if (["contain", "contain_width", "contain_height"].includes(value)) return value;
  return "cover";
};

const normalizeImageSize = (size) => {
  const value = String(size || "medium").toLowerCase();
  if (["small", "medium", "large", "xlarge"].includes(value)) return value;
  return "medium";
};

const imageSizeContainerClass = (size) => {
  const normalized = normalizeImageSize(size);
  if (normalized === "small") return "min-h-[180px]";
  if (normalized === "large") return "min-h-[300px]";
  if (normalized === "xlarge") return "min-h-[380px]";
  return "min-h-[220px]";
};

const imageFitClass = (fit) => {
  const normalized = normalizeImageFit(fit);
  if (normalized === "contain") return "object-contain bg-slate-100";
  if (normalized === "contain_width") return "object-contain object-top bg-slate-100";
  if (normalized === "contain_height") return "object-contain object-center bg-slate-100";
  return "object-cover";
};

const parseLayoutConfig = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_err) {
      return {};
    }
  }
  return {};
};

const normalizeSectionKey = (value) => String(value || "").trim().toLowerCase();

const isAboutContentKey = (key) => {
  const normalized = normalizeSectionKey(key);
  if (!normalized) return false;
  if (normalized === "about_hero") return true;
  if (normalized === "about_affiliated_meta") return true;
  if (normalized.startsWith("about_affiliated_body_")) return true;
  if (normalized.startsWith("about_")) return true;
  if (normalized.startsWith("home_")) return false;
  if (normalized.startsWith("why_join_")) return false;
  if (normalized.startsWith("updates_")) return false;
  return true;
};

const isAchievementsBlock = (item) => {
  const key = normalizeSectionKey(item?.section_key);
  const heading = normalizeSectionKey(item?.heading);

  return (
    key === "achievements" ||
    key === "landmark_achievements" ||
    heading === "our landmark achievements" ||
    heading.startsWith("our landmark achievements")
  );
};

const HOME_DUPLICATE_KEYS = new Set([
  "achievements",
  "landmark_achievements",
  "jobs",
  "dsc",
  "mega_dsc",
  "timeline",
  "medal_tally",
  "vision",
  "future_vision",
  "champions",
  "policy",
  "initiatives",
]);

const isHomeDuplicateBlock = (item) => {
  const key = normalizeSectionKey(item?.section_key);
  const heading = normalizeSectionKey(item?.heading);
  const subheading = normalizeSectionKey(item?.subheading);

  if (HOME_DUPLICATE_KEYS.has(key)) return true;
  if (isAchievementsBlock(item)) return true;

  return [
    heading,
    subheading,
  ].some((value) =>
    [
      "landmark achievements",
      "medal tally",
      "government jobs",
      "future vision",
      "our champions",
      "sports policy",
      "mega dsc",
    ].some((token) => value.includes(token))
  );
};

const extractPhone = (text) => {
  const raw = String(text || "").trim();
  if (!raw) return "-";

  const match = raw.match(/(?:phone|mobile|contact)\s*:\s*([^\n]+)/i);
  if (match?.[1]) return match[1].trim();

  const firstLine = raw.split(/\r?\n/).map((line) => line.trim()).find(Boolean);
  return firstLine || "-";
};

const CHAITANYA_PHASES = [
  {
    phase: "Phase I - Uttarandhra",
    districts: "Srikakulam, Vizianagaram, Visakhapatnam",
  },
  {
    phase: "Phase II - Godavari & Central Coastal",
    districts: "Kakinada, Rajahmundry, Amalapuram, Bhimavaram, Akividu, Eluru, Vijayawada",
  },
  {
    phase: "Phase III - South Coastal & Rayalaseema",
    districts:
      "Tenali, Bapatla, Chirala, Ongole, Kavali, Nellore, Gudur, Tirupati, Chittoor, Mandapalle, Rayachoti, Kadapa, Ananthapur, Kurnool, Nandyala",
  },
];

const CHAITANYA_HIGHLIGHTS = [
  "Equal cash incentives for Para athletes on par with general athletes under new sports policy.",
  "G.O. Ms. No. 4 clarified 3% horizontal reservation opportunities and sports-merit job pathways.",
  "Proposed world-class Para Sports stadium in Visakhapatnam (20 acres, Rs 200 crores).",
];

const ATHLETE_SPOTLIGHTS = [
  {
    name: "Rongali Ravi",
    discipline: "Para Athletics",
    highlight: "5th at Paris Paralympics 2024 and Asian Para Games silver medalist.",
    image: "/images/parasports.png",
    flag: "🏅",
    color: "from-blue-600 to-indigo-700",
  },
  {
    name: "Konganapalli Narayana",
    discipline: "Para Rowing",
    highlight: "Represented at Paris Paralympics 2024 and finished 12th.",
    image: "/images/parasports.png",
    flag: "🚣",
    color: "from-cyan-600 to-blue-700",
  },
  {
    name: "Killaka Lalitha",
    discipline: "Para Athletics (T11)",
    highlight: "Asian Para Games 2023 silver medalist in 1500m, from Vizianagaram district.",
    image: "/images/parasports.png",
    flag: "🏃",
    color: "from-emerald-600 to-teal-700",
  },
];

const appendSectionText = (section) =>
  [
    section?.section_key,
    section?.heading,
    section?.subheading,
    section?.body,
    section?.cta_label,
    section?.cta_link,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");

const AboutUs = () => {
  const [sections, setSections] = useState([]);
  const [showAffiliatedDetails, setShowAffiliatedDetails] = useState(false);
  const [medalSummary, setMedalSummary] = useState(null);

  useEffect(() => {
    const loadSections = async () => {
      try {
        const rows = await API.fetchAboutSections("about_us");
        setSections(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Failed to load about sections", error);
        setSections([]);
      }
    };
    loadSections();
  }, []);

  useEffect(() => {
    const loadMedalSummary = async () => {
      try {
        const response = await API.fetchHomeMedalSummary();
        setMedalSummary(response || null);
      } catch (error) {
        console.error("Failed to load medal summary", error);
        setMedalSummary(null);
      }
    };

    loadMedalSummary();

    const timer = setInterval(async () => {
      try {
        const response = await API.fetchHomeMedalSummary();
        setMedalSummary(response || null);
      } catch (error) {
        console.error("Failed to refresh medal summary", error);
      }
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  const displaySections = useMemo(() => {
    const source = sections.length ? sections : FALLBACK_SECTIONS;
    const filtered = source
      .filter((item) => {
        const key = normalizeSectionKey(item?.section_key);
        if (!isAboutContentKey(key)) return false;
        if (isHomeDuplicateBlock(item)) return false;
        if (key === "about_hero") return false;
        if (key === "about_affiliated_meta") return false;
        if (key.startsWith("about_affiliated_body_")) return false;
        return true;
      })
      .filter((item) => item?.is_active !== false)
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

    const seenKeys = new Set();
    return filtered.filter((item) => {
      const key = normalizeSectionKey(item?.section_key || item?.id);
      if (!key) return true;
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
  }, [sections]);

  const aboutHero = useMemo(() => {
    const found = sections.find(
      (item) => normalizeSectionKey(item?.section_key) === "about_hero"
    );
    return found || null;
  }, [sections]);

  const affiliatedMeta = useMemo(() => {
    const found = sections.find(
      (item) => normalizeSectionKey(item?.section_key) === "about_affiliated_meta"
    );
    return found || null;
  }, [sections]);

  const affiliatedBodies = useMemo(() => {
    const rows = (Array.isArray(sections) ? sections : [])
      .filter((item) => item?.is_active !== false)
      .filter((item) => normalizeSectionKey(item?.section_key).startsWith("about_affiliated_body_"))
      .sort((a, b) => Number(a?.sort_order || 0) - Number(b?.sort_order || 0))
      .map((item, index) => {
        const typeLabel = String(item?.subheading || "Other").trim() || "Other";
        const bucket = /district/i.test(typeLabel) ? "district" : "other";
        return {
          id: item?.id || `${item?.section_key || "about-affiliated"}-${index}`,
          name: String(item?.heading || "").trim() || `Affiliated Body ${index + 1}`,
          typeLabel,
          bucket,
          phone: extractPhone(item?.body),
        };
      });

    return rows;
  }, [sections]);

  const affiliatedCounts = useMemo(() => {
    const districts = affiliatedBodies.filter((row) => row.bucket === "district").length;
    const others = affiliatedBodies.filter((row) => row.bucket !== "district").length;
    return { districts, others, total: districts + others };
  }, [affiliatedBodies]);

  const aboutHeroImage = resolveAssetUrl(aboutHero?.image_path || "", {
    folder: "page-sections",
    defaultPath: "/images/image-fallback.svg",
  });

  const sourceCoverage = useMemo(() => {
    const blob = sections.map((section) => appendSectionText(section)).join(" ");
    const hasAny = (...tokens) => tokens.some((token) => blob.includes(String(token).toLowerCase()));

    return {
      chaitanya: hasAny("chaitanya yatra", "chaitanya"),
      spotlights: hasAny("athlete spotlight", "our champions", "rongali ravi", "killaka lalitha", "konganapalli"),
      gallery: hasAny("photo gallery", "gallery"),
      appendices: hasAny("appendices", "acknowledg"),
    };
  }, [sections]);

  const nationalMedals = getLiveNationalMedals(medalSummary);
  const internationalMedals = getLiveInternationalMedals(medalSummary);
  const totalMedals = getLiveDocumentedMedalTotal(medalSummary);

  return (
    <div className="bg-slate-50">
      <Navbar />

      <section className="relative h-[55vh] flex items-center justify-center text-center text-white px-4">
        <div className="absolute inset-0 bg-black">
          <img
            src={aboutHeroImage}
            alt=" Para Sports Accociation of Andhra Pradesh overview"
            className="w-full h-full object-cover opacity-40"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/image-fallback.svg";
            }}
          />
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
            {aboutHero?.heading || "About  Para Sports Accociation of Andhra Pradesh"}
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mx-auto opacity-90 mt-4">
            {aboutHero?.body ||
              "A decade of para sports excellence, inclusion, and impact in Andhra Pradesh."}
          </p>
        </div>
      </section>

      <section className="relative -mt-8 z-10 max-w-screen-xl mx-auto px-6">
        <div className="grid md:grid-cols-3 overflow-hidden rounded-2xl shadow-xl border border-white/30">
          {[
            ["Our Mission", "Create accessible pathways for athletes with disabilities—from grassroots discovery to podium performance."],
            ["Our Vision", "An Andhra Pradesh where every para-athlete has the opportunity, support and confidence to excel."],
            ["Our Values", "Inclusion, integrity, athlete-first care, excellence and respect guide every programme we build."],
          ].map(([title, text], index) => <article key={title} className={`p-7 ${index === 1 ? "bg-emerald-700 text-white" : "bg-white text-slate-900"}`}><p className={`text-xs font-black uppercase tracking-[.16em] ${index === 1 ? "text-emerald-100" : "text-emerald-700"}`}>Our purpose</p><h2 className="mt-2 text-2xl font-black">{title}</h2><p className={`mt-3 leading-relaxed text-sm ${index === 1 ? "text-emerald-50" : "text-slate-600"}`}>{text}</p></article>)}
        </div>
      </section>

      <section className="py-14 md:py-18"><div className="max-w-screen-xl mx-auto px-6"><div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5"><div><p className="text-emerald-700 text-xs font-black uppercase tracking-[.18em]">Our journey</p><h2 className="text-3xl md:text-4xl font-black text-slate-900 mt-2">Built on participation. Driven by possibility.</h2></div><p className="max-w-xl text-slate-600">We connect athletes, coaches, districts and institutions to turn talent into sustained opportunity—on and beyond the field of play.</p></div><div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">{[["2015","Association established"],[`${totalMedals}+`,"Documented medals"],[`${nationalMedals}+`,"National medals"],[`${internationalMedals}+`,"International medals"]].map(([number,label]) => <div key={label} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm"><strong className="block text-3xl font-black text-emerald-700">{number}</strong><span className="mt-2 text-xs font-bold text-slate-600">{label}</span></div>)}</div></div></section>

      <section className="py-16 md:py-20">
        <div className="max-w-screen-xl mx-auto px-6">
          <div className="space-y-12">
            {displaySections.map((section, index) => {
              const key = normalizeSectionKey(section.section_key);
              let defaultImg = "/images/image-fallback.svg";
              if (key.includes("intro") || key.includes("about")) {
                defaultImg = "/images/parasports.png";
              } else if (key.includes("vision")) {
                defaultImg = "/images/parasports.png";
              } else if (key.includes("policy")) {
                defaultImg = "/images/saaplogo.png";
              } else if (key.includes("initiative")) {
                defaultImg = "/images/pcilogo.png";
              }

              const imageUrl = resolveAssetUrl(section.image_path || "", {
                folder: "page-sections",
                defaultPath: defaultImg,
              });
              const reverse = index % 2 === 1;
              const cfg = parseLayoutConfig(section.layout_config);
              const fitClass = imageFitClass(cfg.image_fit);
              const sizeClass = imageSizeContainerClass(cfg.image_size);
              const showImage = cfg.show_image !== false;
              const showSubheading = cfg.show_subheading !== false;
              const showHeading = cfg.show_heading !== false;
              const showBody = cfg.show_body !== false;

              return (
                <article
                  key={section.id || section.section_key || index}
                  className={`grid grid-cols-1 ${
                    showImage ? "lg:grid-cols-2" : ""
                  } gap-8 lg:gap-12 items-center bg-white rounded-2xl shadow-lg p-6 md:p-10 ${
                    reverse && showImage ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div>
                    {showSubheading && section.subheading ? (
                      <p className="text-sm md:text-base font-semibold text-blue-600 uppercase tracking-wide">
                        {section.subheading}
                      </p>
                    ) : null}
                    {showHeading ? (
                      <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mt-2">
                        {section.heading}
                      </h2>
                    ) : null}
                    {showBody && section.body ? (
                      <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line">
                        {section.body}
                      </p>
                    ) : null}
                    {section.cta_label && section.cta_link ? (
                      <a
                        href={section.cta_link}
                        className="inline-block mt-6 px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                      >
                        {section.cta_label}
                      </a>
                    ) : null}
                  </div>

                  {showImage ? (
                    <div className={`rounded-xl overflow-hidden shadow-md bg-slate-100 ${sizeClass}`}>
                      <img
                        src={imageUrl}
                        alt={section.heading}
                        className={`w-full h-full ${fitClass}`}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = "/images/image-fallback.svg";
                        }}
                      />
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {!sourceCoverage.chaitanya ? (
        <section className="pb-16 md:pb-20">
          <div className="max-w-screen-xl mx-auto px-6">
            <article className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-200">
              <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 text-center">
                Impact and Community Engagement
              </h2>
              <p className="text-slate-600 mt-3 max-w-3xl mx-auto text-center">
                Para Sports Chaitanya Yatra expanded awareness across Andhra Pradesh and built a stronger grassroots pipeline.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-8">
                {CHAITANYA_PHASES.map((phase) => (
                  <div key={phase.phase} className="rounded-xl bg-blue-50 border border-blue-100 p-5">
                    <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">{phase.phase}</p>
                    <p className="text-slate-700 mt-2">{phase.districts}</p>
                  </div>
                ))}
              </div>

              <ul className="mt-8 space-y-2 text-slate-700 list-disc pl-5">
                {CHAITANYA_HIGHLIGHTS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          </div>
        </section>
      ) : null}

      {!sourceCoverage.spotlights ? (
        <section className="pb-16 md:pb-20">
          <div className="max-w-screen-xl mx-auto px-6">
            <article className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-200">
              <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 text-center">
                Athlete Spotlights
              </h2>
              <p className="text-slate-600 mt-3 max-w-3xl mx-auto text-center">
                Champions highlighted in the source document and state records.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
                {ATHLETE_SPOTLIGHTS.map((athlete) => (
                  <article key={athlete.name} className="rounded-xl overflow-hidden border border-slate-200 shadow-md bg-white flex flex-col">
                    <div className={`relative h-36 bg-gradient-to-br ${athlete.color || 'from-blue-600 to-indigo-700'} flex items-center justify-center`}>
                      <img
                        src={athlete.image || "/images/parasports.png"}
                        alt={athlete.name}
                        className="absolute inset-0 w-full h-full object-contain object-center opacity-20 p-4"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <div className="relative z-10 text-center">
                        <span className="text-5xl">{athlete.flag || "🏅"}</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent h-12" />
                    </div>
                    <div className="p-5 flex flex-col flex-grow">
                      <h3 className="text-xl font-bold text-slate-900">{athlete.name}</h3>
                      <p className="text-sm font-semibold text-blue-700 mt-1">{athlete.discipline}</p>
                      <p className="text-slate-600 mt-3 flex-grow">{athlete.highlight}</p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/players"
                  className="inline-block px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                >
                  View More Athletes
                </Link>
              </div>
            </article>
          </div>
        </section>
      ) : null}

      {!sourceCoverage.gallery ? (
        <section className="pb-16 md:pb-20">
          <div className="max-w-screen-xl mx-auto px-6">
            <article className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-200">
              <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 text-center">
                Photo Gallery and News
              </h2>
              <p className="text-slate-600 mt-3 max-w-3xl mx-auto text-center">
                Explore visual highlights and media coverage from competitions, training, and outreach campaigns.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                <Link
                  to="/gallery"
                  className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors text-center"
                >
                  Open Photo Gallery
                </Link>
                <Link
                  to="/news"
                  className="px-6 py-2 rounded-lg bg-slate-700 text-white font-semibold hover:bg-slate-800 transition-colors text-center"
                >
                  Read News Clips
                </Link>
              </div>
            </article>
          </div>
        </section>
      ) : null}

      {!sourceCoverage.appendices ? (
        <section className="pb-16 md:pb-20">
          <div className="max-w-screen-xl mx-auto px-6">
            <article className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-200">
              <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 text-center">
                Appendices and Acknowledgments
              </h2>
              <p className="text-slate-600 mt-3 max-w-3xl mx-auto text-center">
                This journey is built by athletes, coaches, families, administrators, government agencies, and community supporters across Andhra Pradesh.
              </p>
              <div className="rounded-xl bg-slate-100 border border-slate-200 p-5 mt-8 text-center">
                <p className="text-sm uppercase tracking-wide font-semibold text-slate-600">Documented Medal Total</p>
                <p className="text-3xl font-extrabold text-slate-900 mt-1">{totalMedals}</p>
                <p className="text-slate-600 mt-1">525 national + 57 international, plus newly added medals.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-left">
                  <div className="rounded-lg bg-white border border-slate-200 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">National Medals</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{nationalMedals}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 px-4 py-3">
                    <p className="text-xs uppercase tracking-wide font-semibold text-slate-500">International Medals</p>
                    <p className="text-2xl font-extrabold text-slate-900 mt-1">{internationalMedals}</p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      ) : null}

      <section className="pb-16 md:pb-20">
        <div className="max-w-screen-xl mx-auto px-6">
          <article className="bg-white rounded-2xl shadow-lg p-6 md:p-10 border border-slate-200">
            <div className="text-center">
              <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900">
                {affiliatedMeta?.heading || "Affiliated Bodies"}
              </h2>
              <p className="text-slate-600 mt-3 max-w-3xl mx-auto">
                {affiliatedMeta?.subheading ||
                  "Overview of affiliated units grouped into Districts and Others."}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="rounded-xl bg-blue-50 border border-blue-100 p-5 text-center">
                <p className="text-sm font-semibold text-blue-700 uppercase tracking-wide">Districts</p>
                <p className="text-3xl font-extrabold text-blue-900 mt-2">{affiliatedCounts.districts}</p>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-5 text-center">
                <p className="text-sm font-semibold text-amber-700 uppercase tracking-wide">Others</p>
                <p className="text-3xl font-extrabold text-amber-900 mt-2">{affiliatedCounts.others}</p>
              </div>
              <div className="rounded-xl bg-slate-100 border border-slate-200 p-5 text-center">
                <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Total</p>
                <p className="text-3xl font-extrabold text-slate-900 mt-2">{affiliatedCounts.total}</p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => setShowAffiliatedDetails((prev) => !prev)}
                className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
              >
                {showAffiliatedDetails ? "Hide Details" : "More Details"}
              </button>
            </div>

            {showAffiliatedDetails ? (
              <div className="mt-8 overflow-x-auto">
                {affiliatedBodies.length === 0 ? (
                  <p className="text-center text-slate-500">No affiliated bodies available yet.</p>
                ) : (
                  <table className="min-w-full border border-slate-200 rounded-lg overflow-hidden">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Affiliated Body</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Type</th>
                        <th className="text-left px-4 py-3 text-sm font-semibold text-slate-700">Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliatedBodies.map((row) => (
                        <tr key={row.id} className="border-t border-slate-200">
                          <td className="px-4 py-3 text-slate-900">{row.name}</td>
                          <td className="px-4 py-3 text-slate-700">{row.typeLabel}</td>
                          <td className="px-4 py-3 text-slate-700">{row.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : null}
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;

